import React, { useCallback, useEffect, useState } from 'react';
import { tenantsAPI } from '../../services/api';
import { AppButton } from '../ui/AppButton';
import { SectionCard } from '../ui/SectionCard';
import { SegmentedTabs } from '../ui/SegmentedTabs';
import { ACTIVITY_FILTERS, actorOf, describeActivity } from './activityText';

const PAGE = 50;

const timeLabel = (iso) => new Date(iso).toLocaleString(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * The organisation's audit trail for its admins: who invited, changed or
 * removed members, used services, exchanged data or exported their data.
 */
export const ActivityPanel = ({ tenant }) => {
  const organisation = tenant.name || tenant.slug;
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchPage = useCallback((before) => tenantsAPI.listActivity(tenant.id, {
    limit: PAGE,
    ...(filter === 'all' ? {} : { category: filter }),
    ...(before ? { before } : {}),
  }), [tenant.id, filter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPage(null);
      setEvents(data?.events || []);
      setNextBefore(data?.nextBefore || null);
      setError(null);
    } catch (loadError) {
      setError(loadError?.message || 'Activity could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(nextBefore);
      setEvents((previous) => [...previous, ...(data?.events || [])]);
      setNextBefore(data?.nextBefore || null);
    } catch (loadError) {
      setError(loadError?.message || 'Older activity could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <SectionCard
      title="Activity"
      subtitle={`What people did in ${organisation}, newest first. Only organisation admins see this.`}
    >
      <div className="flex flex-col gap-4">
        <div className="overflow-x-auto">
          <SegmentedTabs ariaLabel="Activity filter" size="sm" tabs={ACTIVITY_FILTERS} active={filter} onChange={setFilter} />
        </div>

        {loading ? (
          <p className="m-0 text-[13px] text-lb-muted">Loading activity…</p>
        ) : error && events.length === 0 ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="m-0 text-[13px] text-lb-critical-text">{error}</p>
            <AppButton variant="secondary" size="sm" onClick={load}>Try again</AppButton>
          </div>
        ) : events.length === 0 ? (
          <p className="m-0 text-[13px] text-lb-muted">Nothing recorded here yet.</p>
        ) : (
          <ol className="m-0 p-0 list-none flex flex-col" aria-label={`Activity in ${organisation}`}>
            {events.map((event) => (
              <li
                key={event.id}
                className="grid grid-cols-1 sm:grid-cols-[150px_minmax(0,1fr)] gap-x-4 gap-y-0.5 py-2.5 border-t border-black/[0.07] first:border-t-0 first:pt-0"
              >
                <time dateTime={event.created_at} className="text-[12px] text-lb-muted tabular-nums">
                  {timeLabel(event.created_at)}
                </time>
                <span className="min-w-0 text-[13px] leading-[1.45] text-lb-body">
                  <span className="font-semibold text-lb-ink">{actorOf(event)}</span>{' '}
                  {describeActivity(event)}
                </span>
              </li>
            ))}
          </ol>
        )}

        {nextBefore && !loading && (
          <div>
            <AppButton variant="secondary" size="sm" loading={loadingMore} onClick={loadMore}>
              Show older activity
            </AppButton>
          </div>
        )}
      </div>
    </SectionCard>
  );
};
